import React from 'react';
import { Handle, Position } from 'reactflow';
import { Box } from '@mui/material';

const THEME = {
    cardBg: '#18181b',
    border: '#333',
    primary: '#2cbb5d',
    accent: '#7ee8a8',
    start: '#38bdf8',
    goal: '#f97316',
    cursor: '#facc15',
    text: '#ffffff',
};

const handleBase = {
    width: 10,
    height: 10,
    borderRadius: '50%',
    border: '2px solid #0a0a0a',
    background: THEME.primary,
};

export default function MarkovNode({ data, selected }) {
    const role = data?.solutionRole || 'none';
    const stationary = data?.stationaryStrength ?? 0;
    const value = data?.stateValue;
    const connectable = data?.connectable === true;

    let borderColor = selected ? THEME.primary : THEME.border;
    let ring = 'none';
    let bg = THEME.cardBg;
    let pulse = false;

    if (role === 'start') {
        borderColor = THEME.start;
        ring = `0 0 22px 3px rgba(56, 189, 248, 0.55)`;
        bg = 'rgba(56, 189, 248, 0.12)';
    } else if (role === 'goal') {
        borderColor = THEME.goal;
        ring = `0 0 22px 3px rgba(249, 115, 22, 0.55)`;
        bg = 'rgba(249, 115, 22, 0.12)';
    } else if (role === 'cursor') {
        borderColor = THEME.cursor;
        ring = `0 0 20px 3px rgba(250, 204, 21, 0.55)`;
        bg = 'rgba(250, 204, 21, 0.1)';
        pulse = true;
    } else if (role === 'visited') {
        borderColor = THEME.primary;
        ring = `0 0 12px 1px rgba(44, 187, 93, 0.3)`;
    } else if (role === 'policy') {
        borderColor = THEME.accent;
        ring = `0 0 16px 2px rgba(126, 232, 168, 0.4)`;
    } else if (stationary > 0) {
        const s = 8 + stationary * 28;
        ring = `0 0 ${s}px ${2 + stationary * 4}px rgba(44, 187, 93, ${0.15 + stationary * 0.35})`;
    }

    const handleStyle = {
        ...handleBase,
        opacity: connectable ? 1 : 0,
        pointerEvents: connectable ? 'auto' : 'none',
    };

    return (
        <Box
            className={pulse ? 'markov-node markov-node--pulse' : 'markov-node'}
            sx={{
                minWidth: 108,
                maxWidth: 160,
                px: 2,
                py: 1.25,
                backgroundColor: bg,
                border: `2px solid ${borderColor}`,
                borderRadius: '14px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                color: THEME.text,
                fontWeight: 600,
                fontSize: 13,
                fontFamily: 'system-ui, sans-serif',
                boxShadow: ring,
                transition: 'box-shadow 0.35s ease, border-color 0.2s, background-color 0.2s',
                cursor: connectable ? 'crosshair' : 'grab',
                '&:active': { cursor: connectable ? 'crosshair' : 'grabbing' },
            }}
        >
            <span>{data?.label ?? data?.id}</span>
            {role === 'start' && (
                <span style={{ fontSize: 9, color: THEME.start, marginTop: 2, letterSpacing: 0.5 }}>
                    START
                </span>
            )}
            {role === 'goal' && (
                <span style={{ fontSize: 9, color: THEME.goal, marginTop: 2, letterSpacing: 0.5 }}>
                    GOAL
                </span>
            )}
            {value != null && role !== 'start' && role !== 'goal' && (
                <span style={{ fontSize: 10, color: THEME.accent, marginTop: 2 }}>
                    V={Number(value).toFixed(2)}
                </span>
            )}

            <Handle id="t-top" type="target" position={Position.Top} style={handleStyle} isConnectable={connectable} />
            <Handle id="s-top" type="source" position={Position.Top} style={{ ...handleStyle, left: '30%' }} isConnectable={connectable} />
            <Handle id="t-bottom" type="target" position={Position.Bottom} style={handleStyle} isConnectable={connectable} />
            <Handle id="s-bottom" type="source" position={Position.Bottom} style={{ ...handleStyle, left: '70%' }} isConnectable={connectable} />
            <Handle id="t-left" type="target" position={Position.Left} style={handleStyle} isConnectable={connectable} />
            <Handle id="s-left" type="source" position={Position.Left} style={{ ...handleStyle, top: '30%' }} isConnectable={connectable} />
            <Handle id="t-right" type="target" position={Position.Right} style={handleStyle} isConnectable={connectable} />
            <Handle id="s-right" type="source" position={Position.Right} style={{ ...handleStyle, top: '70%' }} isConnectable={connectable} />
        </Box>
    );
}
